import { PercentPipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

import { RecipientReportDataModel } from '../RecipientReport/recipient.report.data.model';
import { DistributionReportStatisticTypesEnum } from '../../../enum/distribution-report-statistic-types.enum';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { StatisticReportSourceType } from '../../../model/statisticsReport/statistic-report-source-type.enum';

type Stat = number | string | 'N/A';
interface StatSection {
    rate: Stat;
    rateChange: Stat;
    unique: Stat;
    total: Stat;
    tooltip: string;
};

interface AdditionalStatistic {
    label: string;
    info: string;
    count: string;
    emphasize: boolean;
}

@Component({
    selector: 'agpr-performance-tracking-card',
    templateUrl: './performance-tracking-card.component.html',
    styleUrls: ['./performance-tracking-card.component.scss'],
    standalone: false
})
export class PerformanceTrackingCardComponent implements OnInit, OnChanges {
    private readonly percentPipe = new PercentPipe('en-US');
    private readonly percentDigitsInfo = '1.1-1';

    @Input() statType: DistributionReportStatisticTypesEnum;
    @Input() trackingEnabled: boolean = true;
    @Input() enableMppEstimates: boolean = false;
    @Input() disabledTooltip: string = null;
    @Input() recipientReportData: RecipientReportDataModel;
    @Input() statisticReportSourceType = StatisticReportSourceType.DISTRIBUTION;

    statTypes = DistributionReportStatisticTypesEnum;
    statisticReportSourceTypeEnum = StatisticReportSourceType;

    uniqueLabel: string = '';
    totalLabel: string = '';
    rateLabel: string = '';
    rateTooltip: string = null;
    rateLabelTooltip: string = null;
    uniqueLabelTooltip: string = null;
    totalLabelTooltip: string = null;
    stats: StatSection = {
        rate: 'N/A',
        rateChange: 'N/A',
        total: 'N/A',
        unique: 'N/A',
        tooltip: null
    };
    data: {
        uniqueCount: number;
        rateChange: number;
        deliveredCount: number;
        totalCount: number;
    } = {
        uniqueCount: 0,
        rateChange: 0,
        deliveredCount: 0,
        totalCount: 0,
    };

    totalStatistics: AdditionalStatistic[] = [];
    uniqueStatistics: AdditionalStatistic[] = [];
    totalExpanded: boolean = false;
    uniqueExpanded: boolean = false;

    showEstimatedTag: boolean = false;

    private hasInit = false;

    constructor(
        private localizationService: LocalizationService,
    ) { }

    ngOnInit(): void {
        this.hasInit = true;
        this.updateCard();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.hasInit) {
            return;
        }

        this.updateCard();
    }

    private updateCard() {
        if (this.trackingEnabled) {
            this.setData();

            this.stats.rate = this.percentPipe.transform(
                (this.data.uniqueCount / this.data.deliveredCount) || 0,
                this.percentDigitsInfo
            );
            this.stats.rateChange = (Math.abs(this.data.rateChange) || '0.0') + '%';
            this.stats.unique = isNaN(this.data.uniqueCount) ? 'N/A' : this.data.uniqueCount;
            this.stats.total = isNaN(this.data.totalCount) ? 'N/A' : this.data.totalCount;
            this.stats.tooltip = null;
        } else {
            this.stats.rate = 'N/A';
            this.stats.rateChange = 'N/A';
            this.stats.unique = 'N/A';
            this.stats.total = 'N/A';
            this.stats.tooltip = this.disabledTooltip;
        }
        this.showEstimatedTag = this.enableMppEstimates
            && (this.statType === DistributionReportStatisticTypesEnum.UNIQUE_OPEN_RATE
                || (this.statisticReportSourceType === StatisticReportSourceType.BRIEFING
                    && this.statType === DistributionReportStatisticTypesEnum.UNIQUE_CLICK_RATE));
        this.setTooltips();
        this.setLabels();
    }

    private setData() {
        this.data.deliveredCount = this.recipientReportData.deliveredCount;

        this.totalStatistics = [];
        this.uniqueStatistics = [];

        const NA = this.localizationService.transform('Common.not.available');

        if (this.statType === DistributionReportStatisticTypesEnum.UNIQUE_CLICK_RATE) {
            this.data.uniqueCount = this.recipientReportData.uniqueClickCount;
            this.data.rateChange = this.recipientReportData.uniqueClickRatePercentageChangeFromBenchmark;
            this.data.totalCount = this.recipientReportData.clickCount;
            if (this.statisticReportSourceType === StatisticReportSourceType.BRIEFING) {
                if (this.enableMppEstimates) {
                    this.data.uniqueCount = this.recipientReportData.estimatedActualUniqueClicks;
                    this.data.totalCount = this.recipientReportData.estimatedActualClicks;
                }
                this.uniqueStatistics.push({
                    label: 'Discover.distribution.statistics.reliableUniqueClicks',
                    info: `Discover.distribution.statistics.reliableUniqueClicks.info.${this.statisticReportSourceType}`,
                    count: isNaN(this.recipientReportData.reliableUniqueClicks) ?
                        NA : this.recipientReportData.reliableUniqueClicks.toLocaleString(),
                    emphasize: !this.enableMppEstimates
                });
                this.uniqueStatistics.push({
                    label: 'Discover.distribution.statistics.mppImpactedUniqueClicks',
                    info: `Discover.distribution.statistics.mppImpactedUniqueClicks.info.${this.statisticReportSourceType}`,
                    count: isNaN(this.recipientReportData.mppClicks) ?
                        NA : this.recipientReportData.mppClicks.toLocaleString(),
                    emphasize: !this.enableMppEstimates
                });
                this.uniqueStatistics.push({
                    label: 'Discover.distribution.statistics.actualUniqueClicks',
                    info: `Discover.distribution.statistics.actualUniqueClicks.info.${this.statisticReportSourceType}`,
                    count: isNaN(this.recipientReportData.estimatedActualUniqueClicks) ?
                        NA : this.recipientReportData.estimatedActualUniqueClicks.toLocaleString(),
                    emphasize: this.enableMppEstimates
                });

                this.totalStatistics.push({
                    label: 'Discover.distribution.statistics.reliableClicks',
                    info: `Discover.distribution.statistics.reliableClicks.info.${this.statisticReportSourceType}`,
                    count: isNaN(this.recipientReportData.reliableClicks) ?
                        NA : this.recipientReportData.reliableClicks.toLocaleString(),
                    emphasize: !this.enableMppEstimates
                });
                this.totalStatistics.push({
                    label: 'Discover.distribution.statistics.mppImpactedClicks',
                    info: `Discover.distribution.statistics.mppImpactedClicks.info.${this.statisticReportSourceType}`,
                    count: isNaN(this.recipientReportData.totalMppClicks) ?
                        NA : this.recipientReportData.totalMppClicks.toLocaleString(),
                    emphasize: !this.enableMppEstimates
                });
                this.totalStatistics.push({
                    label: 'Discover.distribution.statistics.actualClicks',
                    info: `Discover.distribution.statistics.actualClicks.info.${this.statisticReportSourceType}`,
                    count: isNaN(this.recipientReportData.estimatedActualClicks) ?
                        NA : this.recipientReportData.estimatedActualClicks.toLocaleString(),
                    emphasize: this.enableMppEstimates
                });
            }
        } else {
            this.data.uniqueCount = this.recipientReportData.uniqueOpenCount;
            this.data.rateChange = this.recipientReportData.uniqueOpenRatioPercentageChangeFromBenchmark;
            this.data.totalCount = this.recipientReportData.openCount;

            if (this.statType === DistributionReportStatisticTypesEnum.UNIQUE_OPEN_RATE) {

                this.uniqueStatistics.push({
                    label: 'Discover.distribution.statistics.reliableUniqueOpens',
                    info: `Discover.distribution.statistics.reliableUniqueOpens.info.${this.statisticReportSourceType}`,
                    count: isNaN(this.recipientReportData.reliableUniqueOpenCount) ?
                        NA : this.recipientReportData.reliableUniqueOpenCount.toLocaleString(),
                    emphasize: !this.enableMppEstimates
                });
                this.uniqueStatistics.push({
                    label: 'Discover.distribution.statistics.mppImpactedUniqueOpens',
                    info: `Discover.distribution.statistics.mppImpactedUniqueOpens.info.${this.statisticReportSourceType}`,
                    count: isNaN(this.recipientReportData.mppOpens) ?
                        NA : this.recipientReportData.mppOpens.toLocaleString(),
                    emphasize: !this.enableMppEstimates
                });
                this.uniqueStatistics.push({
                    label: 'Discover.distribution.statistics.actualUniqueOpens',
                    info: `Discover.distribution.statistics.actualUniqueOpens.info.${this.statisticReportSourceType}`,
                    count: isNaN(this.recipientReportData.estimatedActualUniqueOpens) ?
                        NA : this.recipientReportData.estimatedActualUniqueOpens.toLocaleString(),
                    emphasize: this.enableMppEstimates
                });

                this.totalStatistics.push({
                    label: 'Discover.distribution.statistics.reliableOpens',
                    info: `Discover.distribution.statistics.reliableOpens.info.${this.statisticReportSourceType}`,
                    count: isNaN(this.recipientReportData.reliableOpens) ?
                        NA : this.recipientReportData.reliableOpens.toLocaleString(),
                    emphasize: !this.enableMppEstimates
                });
                this.totalStatistics.push({
                    label: 'Discover.distribution.statistics.mppImpactedOpens',
                    info: `Discover.distribution.statistics.mppImpactedOpens.info.${this.statisticReportSourceType}`,
                    count: isNaN(this.recipientReportData.mppOpens) ?
                        NA : this.recipientReportData.mppOpens.toLocaleString(),
                    emphasize: !this.enableMppEstimates
                });
                this.totalStatistics.push({
                    label: 'Discover.distribution.statistics.actualOpens',
                    info: `Discover.distribution.statistics.actualOpens.info.${this.statisticReportSourceType}`,
                    count: isNaN(this.recipientReportData.estimatedActualOpens) ?
                        NA : this.recipientReportData.estimatedActualOpens.toLocaleString(),
                    emphasize: this.enableMppEstimates
                });
                this.setCounts();
            }
        }
    }

    private setTooltips() {
        this.rateTooltip = this.getLocalizedTooltipForBenchmarkComparison(
            this.data.rateChange,
            this.statType
        );

        if (this.statType === DistributionReportStatisticTypesEnum.UNIQUE_CLICK_RATE) {
            this.rateLabelTooltip = `Discover.distributionReport.performance.clicks.rate.tooltip.${this.statisticReportSourceType}`;
            this.uniqueLabelTooltip = `Discover.distributionReport.performance.clicks.unique.tooltip.${this.statisticReportSourceType}`;
            this.totalLabelTooltip = `Discover.distributionReport.performance.clicks.total.tooltip.${this.statisticReportSourceType}`;
        } else {
            this.rateLabelTooltip = `Discover.distributionReport.performance.opens.rate.tooltip.${this.statisticReportSourceType}`;
            this.uniqueLabelTooltip = `Discover.distributionReport.performance.opens.unique.tooltip.${this.statisticReportSourceType}`;
            this.totalLabelTooltip = `Discover.distributionReport.performance.opens.total.tooltip.${this.statisticReportSourceType}`;
        }
    }

    private setLabels() {
        if (this.statType === DistributionReportStatisticTypesEnum.UNIQUE_CLICK_RATE) {
            this.uniqueLabel = 'Discover.distributionReport.recipient.statsType.uniqueClicks';
            this.totalLabel = 'Discover.distributionReport.recipient.statsType.totalClicks';
            this.rateLabel = 'Discover.distributionReport.recipient.statsType.clickRate';
        } else {
            this.uniqueLabel = 'Discover.distributionReport.recipient.statsType.uniqueOpens';
            this.totalLabel = 'Discover.distributionReport.recipient.statsType.totalOpens';
            this.rateLabel = 'Discover.distributionReport.recipient.statsType.openRate';
        }
    }

    private getLocalizedTooltipForBenchmarkComparison(differenceOverBenchmark?: number, statisticType?: DistributionReportStatisticTypesEnum): string {
        let description = this.localizationService.transform('Discover.targeting.distribution.recipient.report.higherThan');
        if (differenceOverBenchmark < 0) {
            description = this.localizationService.transform('Discover.targeting.distribution.recipient.report.lowerThan');
        }

        return this.localizationService.transform(
            'Discover.targeting.distribution.recipient.report.benchmarkStatisticComparison.tooltip',
            {
                'statisticType': this.localizationService.transform(`Discover.targeting.distribution.statistic.lowercase.${statisticType}`),
                'value': `${Math.abs(differenceOverBenchmark)}%`,
                'description': description,
                'benchmarkType':  this.localizationService.transform('Discover.targeting.distribution.recipient.report.average')
            }
        );
    }

    toggleUniqueExpand() {
        this.uniqueExpanded = !this.uniqueExpanded;
    }

    toggleTotalExpand() {
        this.totalExpanded = !this.totalExpanded;
    }

    setCounts() {
        if (this.enableMppEstimates) {
            this.data.totalCount = this.recipientReportData.estimatedActualOpens;
            this.data.uniqueCount = this.recipientReportData.estimatedActualUniqueOpens;
            this.data.rateChange = this.recipientReportData.estimatedUniqueOpenRatioPercentageChangeFromBenchmark;
        } else {
            this.data.totalCount = this.recipientReportData.openCount;
            this.data.uniqueCount = this.recipientReportData.uniqueOpenCount;
            this.data.rateChange = this.recipientReportData.uniqueOpenRatioPercentageChangeFromBenchmark;
        }
    }
}
